import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { FeatureFlag, TextKey } from '@mews-commander/enums';
import { useCultureCode } from '@mews-commander/core';
import { LocalizedCurrencyValue, useFeatureFlag } from '@mews/framework';
import { getCurrencyLocalizationInfo } from '@mews-commander/core/legacy-globalization';
import { useCommanderSelector } from '@mews-commander/utils';
import { CityLedgerHighlightsResult } from '@mews-commander/data-access';
import { HighlightCard } from '../../../../common/HighlightCard';
import { WidgetCardsWrapper } from '../../../../common/WidgetCardsWrapper';
import { AgeingCard } from '../AgeingCard';
import { InvoiceStatusCard } from '../InvoiceStatusCard';
import { useCityLedgerHighlights } from '../../../hooks/useCityLedgerHighlights';
import { useCityLedgerSelectedDate } from '../../../context/CityLedgerFilterContext';

interface WidgetCardsProps {
    highlights?: CityLedgerHighlightsResult | null;
}

export const WidgetCards = ({ highlights }: WidgetCardsProps) => {
    const enableFilteringHighlights = useFeatureFlag(FeatureFlag.filteringHighlightsInCityLedger);
    const enterprise = useCurrentEnterprise();
    const { selectedDate } = useCityLedgerSelectedDate();
    const {
        outstandingBalance: highlightOutstandingBalance,
        statusDistribution: highlightStatusDistribution,
        ageingDistribution: highlightAgeingDistribution,
    } = highlights || {};

    const {
        data: { outstandingBalance = null, statusDistribution = null, ageingDistribution = null } = {},
        isLoading,
        error,
    } = useCityLedgerHighlights();

    const cultureCode = useCultureCode();
    const currencyLocalizationInfo = useCommanderSelector((state) =>
        getCurrencyLocalizationInfo(state, { cultureCode, currencyCode: enterprise.defaultCurrencyCode })
    );

    const filteredOutstandingBalance = enableFilteringHighlights ? highlightOutstandingBalance : outstandingBalance;
    const filteredAgeingDistribution = enableFilteringHighlights ? highlightAgeingDistribution : ageingDistribution;
    const filteredStatusDistribution = enableFilteringHighlights ? highlightStatusDistribution : statusDistribution;

    return (
        <WidgetCardsWrapper cardMaxWidth="33%" padding="0">
            <HighlightCard
                title={TextKey.OutstandingBalance}
                date={!enableFilteringHighlights ? selectedDate : undefined}
                content={filteredOutstandingBalance && <LocalizedCurrencyValue value={filteredOutstandingBalance} />}
                loading={isLoading}
                error={error}
            />
            <InvoiceStatusCard
                data={filteredStatusDistribution}
                loading={isLoading}
                error={error}
                date={!enableFilteringHighlights ? selectedDate : undefined}
            />
            <AgeingCard
                currencySymbol={currencyLocalizationInfo.symbol}
                data={filteredAgeingDistribution}
                loading={isLoading}
                error={error}
                date={!enableFilteringHighlights ? selectedDate : undefined}
            />
        </WidgetCardsWrapper>
    );
};
